import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import mapboxgl from 'mapbox-gl';
import type { MarkerPreceptor } from './types';

interface UseMapboxMapOptions {
  mapContainer: MutableRefObject<HTMLDivElement | null>;
  losAngelesOnly: boolean;
  centerOverride?: [number, number];
  userLocation?: { lat: number; lng: number };
  preceptors: MarkerPreceptor[];
  zoom: number;
  detailView: boolean;
  lockZoom?: boolean;
  fixedZoomLevel?: number;
  onBoundsChange?: (bounds: { north: number; south: number; east: number; west: number }) => void;
  autoFit?: boolean;
}

export const useMapboxMap = ({
  mapContainer,
  losAngelesOnly,
  centerOverride,
  userLocation,
  preceptors,
  zoom,
  detailView,
  lockZoom,
  fixedZoomLevel,
  onBoundsChange,
  autoFit,
}: UseMapboxMapOptions) => {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [currentZoom, setCurrentZoom] = useState<number>(12);
  const userInteractedRef = useRef(false);
  const lastReportedBoundsRef = useRef<{ north: number; south: number; east: number; west: number } | null>(null);

  useEffect(() => {
    if (mapRef.current || !mapContainer.current || !mapboxgl.accessToken) return;
    const LA: [number, number] = [-118.243683, 34.052235];
    const US: [number, number] = [-98.5795, 39.8283];
    const USA_ZOOM = 4; // When zoom is 4, we're showing all USA - don't override center
    let center: [number, number] = losAngelesOnly ? LA : (centerOverride || US);
    // Only override the US center with preceptor/user location when we're NOT in USA overview mode (zoom !== 4)
    if (!losAngelesOnly && !centerOverride && zoom !== USA_ZOOM) {
      if (userLocation) center = [userLocation.lng, userLocation.lat];
      else if (preceptors.length) {
        const first = preceptors[0];
        if (typeof first.longitude === 'number' && typeof first.latitude === 'number') {
          center = [first.longitude, first.latitude];
        }
      }
    }
    let initialZoom = losAngelesOnly ? 12 : zoom;
    if (!losAngelesOnly && preceptors.length === 1 && zoom !== USA_ZOOM) initialZoom = 15;

    mapRef.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: detailView
        ? (process.env.NEXT_PUBLIC_MAPBOX_DETAIL_STYLE_URL || 'mapbox://styles/mapbox/light-v11')
        : (process.env.NEXT_PUBLIC_MAPBOX_STYLE_URL || 'mapbox://styles/mapbox/light-v11'),
      // Force flat mercator projection. mapbox-gl v3 defaults to a 3D globe at low
      // zoom, which made the all-sites overview render as a zoomed-out globe.
      projection: 'mercator',
      center,
      zoom: initialZoom,
      pitch: 0,
      bearing: 0,
      interactive: true,
      minZoom: lockZoom && fixedZoomLevel ? fixedZoomLevel : undefined,
      maxZoom: lockZoom && fixedZoomLevel ? fixedZoomLevel : undefined,
    });

    let detachInteractions: (() => void) | undefined;
    mapRef.current.once('load', () => {
      if (centerOverride) {
        const zoomTarget = mapRef.current?.getZoom() ?? initialZoom;
        mapRef.current?.jumpTo({ center: centerOverride, zoom: zoomTarget });
      }
      setMapLoaded(true);

      if (mapRef.current) {
        setCurrentZoom(mapRef.current.getZoom());
        
        // Throttled zoom update for smoother transitions
        let lastUpdate = 0;
        const throttleMs = 50; // Update at most every 50ms during zoom
        
        mapRef.current.on('zoom', () => {
          if (mapRef.current) {
            const now = Date.now();
            if (now - lastUpdate >= throttleMs) {
              lastUpdate = now;
              setCurrentZoom(mapRef.current.getZoom());
            }
          }
        });
        
        // Ensure final zoom value is captured
        mapRef.current.on('zoomend', () => {
          if (mapRef.current) {
            setCurrentZoom(mapRef.current.getZoom());
          }
        });
      }

      if (onBoundsChange && mapRef.current) {
        const handleBoundsChange = () => {
          if (!mapRef.current) return;
          // Always report bounds so list syncs with map viewport
          // The parent component debounces updates to prevent rapid re-renders
          const bounds = mapRef.current.getBounds();
          if (!bounds) return;
          const next = {
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest(),
          };
          const prev = lastReportedBoundsRef.current;
          const epsilon = 1e-6;
          const same = prev &&
            Math.abs(prev.north - next.north) < epsilon &&
            Math.abs(prev.south - next.south) < epsilon &&
            Math.abs(prev.east - next.east) < epsilon &&
            Math.abs(prev.west - next.west) < epsilon;
          if (same) return;
          lastReportedBoundsRef.current = next;
          onBoundsChange(next);
        };
        mapRef.current.on('moveend', handleBoundsChange);
        handleBoundsChange();
      }

      const markInteracted = () => { userInteractedRef.current = true; };
      const events = ['dragstart', 'zoomstart', 'rotatestart', 'movestart', 'wheel', 'touchstart', 'mousedown'];
      events.forEach(evt => mapRef.current?.on(evt, markInteracted));
      detachInteractions = () => {
        events.forEach(evt => mapRef.current?.off(evt, markInteracted));
      };
    });

    return () => {
      if (detachInteractions) detachInteractions();
    };
  }, [mapContainer, losAngelesOnly, centerOverride, userLocation, preceptors, zoom, detailView, lockZoom, fixedZoomLevel, onBoundsChange, autoFit]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !mapContainer.current) return;
    const map = mapRef.current;
    const ro = new ResizeObserver(() => {
      if (mapRef.current && mapContainer.current) {
        try { map.resize(); } catch (e) { /* swallow resize after removal */ }
      }
    });
    ro.observe(mapContainer.current);
    return () => {
      ro.disconnect();
    };
  }, [mapLoaded, mapContainer]);

  return { mapRef, mapLoaded, currentZoom, userInteractedRef };
};
